import { useState } from 'react';
import { useDaten, euro } from '../../../gemeinsam/lib/daten';
import type { Buchhaltung as DatenBasis } from '../../../gemeinsam/lib/typen';
type Daten = DatenBasis & { ausgaben?: { netto: number; vorsteuer: number; anzahl: number }; gewinn?: number };
import { Hinweis, Karte, KartenKopf, Spinner, TarifSperre, LeerZustand } from '../../../gemeinsam/komponenten/Basis';
import Finanzlage from '../komponenten/Finanzlage';
import Fixkosten from '../komponenten/Fixkosten';
import AusgabenSeite from './Ausgaben';
import SteuerberaterExport from '../komponenten/SteuerberaterExport';
import { useLocation } from 'react-router-dom';

// ── Zeitraum für Umsatz und Gewinn ───────────────────────────────────────────

type Vorgabe = 'woche' | 'vorwoche' | 'monat' | 'vormonat' | 'quartal' | 'vorquartal' | 'jahr' | 'vorjahr' | '12m' | 'eigen';
type Gruppierung = 'woche' | 'monat' | 'quartal';

const VORGABEN: Array<[Vorgabe, string, Gruppierung]> = [
  ['woche', 'Diese Woche', 'woche'], ['vorwoche', 'Letzte Woche', 'woche'],
  ['monat', 'Dieser Monat', 'woche'], ['vormonat', 'Letzter Monat', 'woche'],
  ['quartal', 'Dieses Quartal', 'monat'], ['vorquartal', 'Letztes Quartal', 'monat'],
  ['jahr', 'Dieses Jahr', 'monat'], ['vorjahr', 'Letztes Jahr', 'quartal'],
  ['12m', 'Letzte 12 Monate', 'monat'], ['eigen', 'Eigener Zeitraum', 'monat'],
];

const zweistellig = (n: number) => String(n).padStart(2, '0');
const isoTag = (d: Date) => `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;

/** Von–bis zur Vorgabe, in Ortszeit. Wochen beginnen montags. */
function bereichFuer(v: Vorgabe, eigen: { von: string; bis: string }): { von: string; bis: string } {
  const h = new Date();
  const tag = (j: number, m: number, t: number) => new Date(j, m, t);
  const montag = tag(h.getFullYear(), h.getMonth(), h.getDate() - ((h.getDay() + 6) % 7));
  const q = Math.floor(h.getMonth() / 3) * 3;
  const b = (von: Date, bis: Date) => ({ von: isoTag(von), bis: isoTag(bis) });
  switch (v) {
    case 'woche': return b(montag, tag(montag.getFullYear(), montag.getMonth(), montag.getDate() + 6));
    case 'vorwoche': return b(tag(montag.getFullYear(), montag.getMonth(), montag.getDate() - 7), tag(montag.getFullYear(), montag.getMonth(), montag.getDate() - 1));
    case 'monat': return b(tag(h.getFullYear(), h.getMonth(), 1), tag(h.getFullYear(), h.getMonth() + 1, 0));
    case 'vormonat': return b(tag(h.getFullYear(), h.getMonth() - 1, 1), tag(h.getFullYear(), h.getMonth(), 0));
    case 'quartal': return b(tag(h.getFullYear(), q, 1), tag(h.getFullYear(), q + 3, 0));
    case 'vorquartal': return b(tag(h.getFullYear(), q - 3, 1), tag(h.getFullYear(), q, 0));
    case 'jahr': return b(tag(h.getFullYear(), 0, 1), tag(h.getFullYear(), 11, 31));
    case 'vorjahr': return b(tag(h.getFullYear() - 1, 0, 1), tag(h.getFullYear() - 1, 11, 31));
    case 'eigen': return eigen;
    default: return b(tag(h.getFullYear(), h.getMonth() - 11, 1), h);
  }
}

type Periode = {
  schluessel: string; label: string; netto: number; brutto: number;
  anzahl: number; gutschriften: number; ausgaben: number; gewinn: number;
};

function Kennzahl({ titel, wert, zusatz, ton }: { titel: string; wert: string; zusatz?: string; ton?: 'gut' | 'warnung' }) {
  const farbe = ton === 'gut' ? 'text-emerald-600 dark:text-emerald-400'
    : ton === 'warnung' ? 'text-amber-600 dark:text-amber-400'
    : 'text-slate-900 dark:text-slate-100';
  return (
    <Karte klasse="p-4">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{titel}</p>
      <p className={`mt-1 text-xl font-bold ${farbe}`}>{wert}</p>
      {zusatz && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{zusatz}</p>}
    </Karte>
  );
}

type EUeR = {
  jahr: number;
  einnahmen: { umsatzerloese: number; umsatzsteuer: number; anzahlBelege: number };
  ausgaben: {
    kategorien: Array<{ id: string; name: string; euerZeile: number; skr03: string; anzahl: number; netto: number; abziehbar: number; gekuerzt: number }>;
    summeAbziehbar: number; vorsteuer: number; anzahlBelege: number;
  };
  ergebnis: { gewinn: number; zahllastUmsatzsteuer: number };
  hinweis: string;
};

type UStVA = {
  zeitraum: { jahr: number; monat: number };
  kennzahlen: Array<{ kz: string; bezeichnung: string; betrag: number }>;
  zahllast: number;
  hinweis: string;
};

export default function Buchhaltung() {
  // Die Finanzlage ist der Einstieg: wer die Buchhaltung öffnet, will zuerst
  // wissen, wie es steht — die Formulare fürs Finanzamt kommen danach.
  type Reiter = 'finanzlage' | 'fixkosten' | 'ausgaben' | 'uebersicht' | 'euer' | 'ustva' | 'steuerberater';
  // Andere Seiten verlinken direkt auf einen Reiter, z. B. /buchhaltung?reiter=ausgaben.
  const ort = useLocation();
  const [reiter, setReiter] = useState<Reiter>(() => {
    const r = new URLSearchParams(ort.search).get('reiter');
    return (['finanzlage', 'fixkosten', 'ausgaben', 'uebersicht', 'euer', 'ustva', 'steuerberater'] as string[]).includes(r || '')
      ? r as Reiter : 'finanzlage';
  });
  const jetzt = new Date();
  // Zeitraum und Gliederung für Umsatz, Ausgaben und Gewinn.
  const [vorgabe, setVorgabe] = useState<Vorgabe>('12m');
  const [eigen, setEigen] = useState({ von: isoTag(new Date(jetzt.getFullYear(), 0, 1)), bis: isoTag(jetzt) });
  const [gruppierung, setGruppierung] = useState<Gruppierung>('monat');
  const bereich = bereichFuer(vorgabe, eigen);
  const { daten, laedt, fehler, tarifsperre } = useDaten<Daten & { perioden?: Periode[]; gruppierung?: Gruppierung }>(
    `/accounting/uebersicht?von=${bereich.von}&bis=${bereich.bis}T23:59:59&gruppierung=${gruppierung}`,
    [bereich.von, bereich.bis, gruppierung]);
  const vorgabeName = VORGABEN.find(v => v[0] === vorgabe)?.[1] ?? '';
  const { daten: euer } = useDaten<EUeR>(`/accounting/euer?jahr=${jetzt.getFullYear()}`);
  const { daten: ustva } = useDaten<UStVA>(
    `/accounting/ustva?jahr=${jetzt.getFullYear()}&monat=${jetzt.getMonth() + 1}`);



  if (tarifsperre) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Buchhaltung</h1>
        <TarifSperre was="Die Buchhaltung" upgradeAuf={tarifsperre.upgradeAuf} />
      </div>
    );
  }
  if (laedt && !daten) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (fehler) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (!daten) return null;


  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Buchhaltung</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {reiter === 'uebersicht'
            ? `${vorgabeName} · ${new Date(bereich.von).toLocaleDateString('de-DE')} – ${new Date(bereich.bis).toLocaleDateString('de-DE')} · ${daten.umsatz.anzahlBelege} Belege`
            : 'Finanzlage, Fixkosten, Ausgaben, Umsatz und Steuern an einem Ort'}
        </p>
      </div>

      <div className="-mx-1 flex gap-1 overflow-x-auto border-b border-slate-200 px-1 dark:border-slate-800">
        {([['finanzlage','Finanzlage'],['fixkosten','Fixkosten'],['ausgaben','Ausgaben'],['uebersicht','Umsatz'],['euer','EÜR'],['ustva','Umsatzsteuer'],['steuerberater','Steuerberater']] as const).map(([id, name]) => (
          <button key={id} onClick={() => setReiter(id)}
            className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              reiter === id
                ? 'border-marke-600 text-marke-700 dark:border-marke-400 dark:text-marke-300'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>
            {name}
          </button>
        ))}
      </div>

      {reiter === 'finanzlage' && <Finanzlage zuFixkosten={() => setReiter('fixkosten')} />}
      {reiter === 'fixkosten' && <Fixkosten />}
      {reiter === 'ausgaben' && <AusgabenSeite eingebettet />}

      {reiter === 'euer' && euer && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Kennzahl titel="Betriebseinnahmen" wert={euro(euer.einnahmen.umsatzerloese)} ton="gut"
                      zusatz={`${euer.einnahmen.anzahlBelege} Rechnungen`} />
            <Kennzahl titel="Betriebsausgaben" wert={euro(euer.ausgaben.summeAbziehbar)}
                      zusatz={`${euer.ausgaben.anzahlBelege} Belege`} />
            <Kennzahl titel={`Gewinn ${euer.jahr}`} wert={euro(euer.ergebnis.gewinn)}
                      ton={euer.ergebnis.gewinn >= 0 ? 'gut' : 'warnung'} zusatz="Anlage EÜR Zeile 91" />
          </div>

          <Karte>
            <KartenKopf titel="Betriebsausgaben nach Kategorie"
                        hinweis="Zeile = Anlage EÜR · Konto = SKR03" />
            {euer.ausgaben.kategorien.length === 0 ? (
              <LeerZustand titel="Noch keine Ausgaben erfasst"
                           text="Ohne Ausgaben ist der Gewinn zu hoch — und damit die Steuer." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-2.5 font-medium">Kategorie</th>
                      <th className="px-3 py-2.5 font-medium">Zeile</th>
                      <th className="px-3 py-2.5 font-medium">SKR03</th>
                      <th className="px-3 py-2.5 text-right font-medium">Netto</th>
                      <th className="px-5 py-2.5 text-right font-medium">Abziehbar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {euer.ausgaben.kategorien.map(k => (
                      <tr key={k.id}>
                        <td className="px-5 py-2.5 text-slate-900 dark:text-slate-100">
                          {k.name}
                          {k.gekuerzt > 0 && (
                            <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                              −{euro(k.gekuerzt)} nicht abziehbar
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">{k.euerZeile}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-500 dark:text-slate-400">{k.skr03}</td>
                        <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400">{euro(k.netto)}</td>
                        <td className="px-5 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">{euro(k.abziehbar)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-semibold dark:bg-slate-800/50">
                      <td className="px-5 py-2.5 text-slate-900 dark:text-slate-100" colSpan={4}>Summe abziehbar</td>
                      <td className="px-5 py-2.5 text-right text-slate-900 dark:text-slate-100">{euro(euer.ausgaben.summeAbziehbar)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Karte>

          <Hinweis art="info">{euer.hinweis}</Hinweis>
        </div>
      )}

      {reiter === 'ustva' && ustva && (
        <div className="space-y-4">
          <Karte>
            <KartenKopf
              titel={`Umsatzsteuer-Voranmeldung ${String(ustva.zeitraum.monat).padStart(2, '0')}/${ustva.zeitraum.jahr}`}
              hinweis="Diese Kennzahlen tragen Sie in ELSTER ein"
            />
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {ustva.kennzahlen.map(k => (
                  <tr key={k.kz}>
                    <td className="px-5 py-3">
                      <span className="mr-3 inline-block rounded bg-marke-100 px-2 py-0.5 font-mono text-xs font-semibold text-marke-800 dark:bg-marke-950 dark:text-marke-300">
                        {k.kz}
                      </span>
                      <span className="text-slate-900 dark:text-slate-100">{k.bezeichnung}</span>
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-slate-900 dark:text-slate-100">{euro(k.betrag)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Karte>

          <Hinweis art={ustva.zahllast > 0 ? 'warnung' : 'info'}
                   titel={ustva.zahllast > 0 ? `Zahllast: ${euro(ustva.zahllast)}` : `Erstattung: ${euro(-ustva.zahllast)}`}>
            {ustva.hinweis}
          </Hinweis>
        </div>
      )}

      {reiter === 'steuerberater' && (
        <div className="space-y-4">
          <SteuerberaterExport />

          <Karte>
            <KartenKopf titel="Für die Betriebsprüfung" />
            <div className="p-5">
              <Hinweis art="info">
                Die GDPdU-Dateien für den Prüfer — inklusive Änderungsprotokoll und
                Beschreibungsstandard — finden Sie unter „GoBD &amp; Prüfung".
              </Hinweis>
            </div>
          </Karte>
        </div>
      )}

      {reiter !== 'uebersicht' ? null : <>
      <Karte>
        <div className="grid gap-3 p-4 sm:grid-cols-[1fr_auto]">
          <div className="flex flex-wrap gap-1.5">
            {VORGABEN.map(([id, name, gr]) => (
              <button key={id} type="button" onClick={() => { setVorgabe(id); setGruppierung(gr); }}
                      className={`rounded-full border px-3 py-1 text-sm transition ${vorgabe === id
                        ? 'border-marke-600 bg-marke-600 text-white'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                {name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-slate-300 p-0.5 dark:border-slate-700" role="group" aria-label="Gliederung">
            {([['woche', 'Wochen'], ['monat', 'Monate'], ['quartal', 'Quartale']] as const).map(([id, name]) => (
              <button key={id} type="button" onClick={() => setGruppierung(id)}
                      className={`rounded-md px-3 py-1 text-sm ${gruppierung === id
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                {name}
              </button>
            ))}
          </div>
          {vorgabe === 'eigen' && (
            <div className="flex flex-wrap items-end gap-3 sm:col-span-2">
              <label className="text-sm text-slate-700 dark:text-slate-300">Von
                <input type="date" value={eigen.von} max={eigen.bis} onChange={e => setEigen(x => ({ ...x, von: e.target.value }))}
                       className="ml-2 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              </label>
              <label className="text-sm text-slate-700 dark:text-slate-300">Bis
                <input type="date" value={eigen.bis} min={eigen.von} onChange={e => setEigen(x => ({ ...x, bis: e.target.value }))}
                       className="ml-2 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              </label>
            </div>
          )}
        </div>
      </Karte>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kennzahl titel="Umsatz netto" wert={euro(daten.umsatz.netto)} zusatz={`${euro(daten.umsatz.brutto)} brutto`} />
        <Kennzahl titel="Betriebsausgaben" wert={euro(daten.ausgaben?.netto ?? 0)}
                  zusatz={`${daten.ausgaben?.anzahl ?? 0} Belege`} />
        <Kennzahl titel="Gewinn" wert={euro(daten.gewinn ?? daten.rohertrag)} ton="gut"
                  zusatz={daten.umsatz.netto > 0 ? `${((daten.rohertrag / daten.umsatz.netto) * 100).toFixed(1)} % Marge` : undefined} />
        <Kennzahl titel="Offene Forderungen" wert={euro(daten.zahlungen.offenBetrag)}
                  ton={daten.zahlungen.ueberfaelligAnzahl > 0 ? 'warnung' : undefined}
                  zusatz={daten.zahlungen.ueberfaelligAnzahl > 0
                    ? `davon ${euro(daten.zahlungen.ueberfaelligBetrag)} überfällig`
                    : `${daten.zahlungen.offenAnzahl} Rechnungen`} />
      </div>

      {daten.zahlungen.ueberfaelligAnzahl > 0 && (
        <Hinweis art="warnung" titel="Überfällige Rechnungen">
          {daten.zahlungen.ueberfaelligAnzahl} {daten.zahlungen.ueberfaelligAnzahl === 1 ? 'Rechnung ist' : 'Rechnungen sind'} über
          das Zahlungsziel hinaus offen — zusammen {euro(daten.zahlungen.ueberfaelligBetrag)}.
        </Hinweis>
      )}

      <Karte>
        <KartenKopf titel="Umsatzsteuer" hinweis="Grundlage für die Voranmeldung" />
        {daten.umsatzsteuer.length === 0 ? (
          <LeerZustand titel="Keine Umsätze im Zeitraum" />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr><th className="px-5 py-2.5 font-medium">Steuersatz</th>
                  <th className="px-5 py-2.5 text-right font-medium">Netto</th>
                  <th className="px-5 py-2.5 text-right font-medium">Steuer</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {daten.umsatzsteuer.map(s => (
                <tr key={s.satz}>
                  <td className="px-5 py-2.5 text-slate-900 dark:text-slate-100">{s.satz} %</td>
                  <td className="px-5 py-2.5 text-right text-slate-600 dark:text-slate-400">{euro(s.netto)}</td>
                  <td className="px-5 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">{euro(s.steuer)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold dark:bg-slate-800/50">
                <td className="px-5 py-2.5 text-slate-900 dark:text-slate-100">Summe</td>
                <td className="px-5 py-2.5 text-right text-slate-900 dark:text-slate-100">{euro(daten.umsatz.netto)}</td>
                <td className="px-5 py-2.5 text-right text-slate-900 dark:text-slate-100">{euro(daten.umsatz.steuer)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Karte>

      <Karte>
        <KartenKopf titel={`Umsatz und Gewinn je ${gruppierung === 'woche' ? 'Woche' : gruppierung === 'quartal' ? 'Quartal' : 'Monat'}`}
                    hinweis="Umsatz netto (Gutschriften abgezogen) · Ausgaben netto · Gewinn = Umsatz − Ausgaben" />
        {!(daten.perioden ?? []).length ? (
          <LeerZustand titel="Keine Daten im Zeitraum" />
        ) : (() => {
          const perioden = daten.perioden ?? [];
          const groesster = Math.max(1, ...perioden.map(p => Math.max(Math.abs(p.netto), Math.abs(p.ausgaben))));
          const summe = perioden.reduce((x, p) => ({ netto: x.netto + p.netto, ausgaben: x.ausgaben + p.ausgaben, gewinn: x.gewinn + p.gewinn, anzahl: x.anzahl + p.anzahl }),
            { netto: 0, ausgaben: 0, gewinn: 0, anzahl: 0 });
          return (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-2.5 font-medium">Zeitraum</th>
                    <th className="px-3 py-2.5 text-right font-medium">Umsatz</th>
                    <th className="px-3 py-2.5 text-right font-medium">Ausgaben</th>
                    <th className="px-3 py-2.5 text-right font-medium">Gewinn</th>
                    <th className="px-5 py-2.5 text-right font-medium">Belege</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {perioden.map(p => (
                    <tr key={p.schluessel}>
                      <td className="px-5 py-2">
                        <span className="text-slate-800 dark:text-slate-200">{p.label}</span>
                        {/* Schlichte Balken: Umsatz oben, Ausgaben darunter — genügt für den Vergleich. */}
                        <div className="mt-1 space-y-0.5">
                          <div className="h-1.5 rounded-full bg-marke-500" style={{ width: `${Math.max(1, (Math.max(0, p.netto) / groesster) * 100)}%` }} />
                          <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${Math.max(1, (p.ausgaben / groesster) * 100)}%` }} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-100">{euro(p.netto)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-400">{euro(p.ausgaben)}</td>
                      <td className={`px-3 py-2 text-right font-medium tabular-nums ${p.gewinn < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{euro(p.gewinn)}</td>
                      <td className="px-5 py-2 text-right text-slate-500 dark:text-slate-400">
                        {p.anzahl}{p.gutschriften ? ` (−${p.gutschriften} GS)` : ''}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold dark:bg-slate-800/50">
                    <td className="px-5 py-2.5 text-slate-900 dark:text-slate-100">Summe</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 dark:text-slate-100">{euro(summe.netto)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 dark:text-slate-100">{euro(summe.ausgaben)}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${summe.gewinn < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{euro(summe.gewinn)}</td>
                    <td className="px-5 py-2.5 text-right text-slate-900 dark:text-slate-100">{summe.anzahl}</td>
                  </tr>
                </tbody>
              </table>
              <p className="px-5 py-2 text-xs text-slate-500 dark:text-slate-400">
                <span className="mr-1 inline-block h-2 w-3 rounded-full bg-marke-500 align-middle" />Umsatz
                <span className="ml-3 mr-1 inline-block h-2 w-3 rounded-full bg-amber-400 align-middle" />Ausgaben
              </p>
            </div>
          );
        })()}
      </Karte>
      </>}
    </div>
  );
}
